import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalSearchService } from './globalsearchservice.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

export interface Tpl3plConfig {
	client_id?: string;
	integration_type: 'API' | 'PGS' | 'EDI' | 'SFTP' | 'FTP';
	enabled: boolean;
	test_mode: boolean;
	warehouse_code: string;
	config_json: {
		api?: {
			baseUrl?: string;
			apiKey?: string;
			apiSecret?: string;
			timeout?: number;
			retryAttempts?: number;
		};
		pgs?: {
			baseUrl?: string;
			userId?: string;
			passwd?: string;
			defaultWarehouse?: string;
			defaultOutboundChannel?: 'B2B' | 'B2C';
			b2cServiceType?: string;
			warehouseMap?: { [key: string]: string };
			b2bB2cMapping?: { ordertype?: { [key: string]: string }; salestype?: { [key: string]: string } };
		};
		edi?: {
			tradingPartnerId?: string;
			isaQualifier?: string;
			gsQualifier?: string;
		};
		sftp?: {
			host?: string;
			port?: number;
			username?: string;
			password?: string;
			remotePath?: string;
			outboundPath?: string;
		};
		mapping?: {
			orderStatus?: { [key: string]: string };
			carrierMapping?: { [key: string]: string };
			b2bB2c?: { ordertype?: { [key: string]: string }; salestype?: { [key: string]: string } };
		};
		allowed_statuses?: number[];
		blocked_statuses?: number[];
		shipped_statuses?: number[];
		auto_invoice_on_shipment?: boolean;
		status_on_shipment_received?: number | string | null;
		status_on_tracking_in_transit?: number | string | null;
		status_on_tracking_delivered?: number | string | null;
		status_on_invoice_failed?: number | string | null;
		sku_categories?: string[];
		sku_lines?: string[];
		sku_manufacturers?: number[];
		syncSchedule?: {
			inventory?: string;
			shipments?: string;
		};
	};
}

export interface ExportQueueItem {
	id: number;
	orderno: string;
	export_status: 'pending' | 'exporting' | 'exported' | 'failed' | 'dead_letter';
	export_attempts: number;
	last_export_attempt: string;
	export_response: any;
	error_message: string;
	created_at: string;
	exported_at: string;
	archived_at?: string | null;
	tpl3pl_remote_order_id?: string | null;
}

export interface Shipment {
	id: number;
	orderno: string;
	tracking_number: string;
	carrier: string;
	service_type?: string;
	ship_date: string;
	delivery_date: string;
	status: string;
	raw_data: any;
	processed: boolean;
	processed_at: string;
	processed_phase?: 'pre_invoice' | 'auto_invoiced' | 'invoice_failed';
	invoice_message?: string;
	created_at: string;
}

export interface InventoryItem {
	id: number;
	stockid: string;
	warehouse_code: string;
	pgs_warehouse?: string;
	quantity_on_hand: number;
	quantity_available: number;
	internal_qoh: number;
	last_sync: string;
	sync_status: 'synced' | 'pending' | 'error';
	error_message: string;
	created_at: string;
	updated_at: string;
	lots?: Array<{
		lot_number: string;
		quantity: number;
		expiration_date: string | null;
		lot_status: string;
		last_sync: string;
	}>;
}

export interface LogEntry {
	id: number;
	log_type: 'order_export' | 'shipment_import' | 'inventory_sync' | 'error' | 'inbound';
	orderno: string;
	stockid: string;
	direction: 'outbound' | 'inbound';
	status: 'success' | 'error' | 'warning';
	message: string;
	request_data: any;
	response_data: any;
	error_details: string;
	created_at: string;
}

@Injectable({
	providedIn: 'root'
})
export class Tpl3plService {
	private config: any;
	user: any = [];
	private configSavedSource = new Subject<void>();
	configSaved$ = this.configSavedSource.asObservable();

	constructor(
		private http: HttpClient,
		private globalSearchService: GlobalSearchService,
		private configSvc: RuntimeConfigLoaderService
	) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
		});

		this.globalSearchService.user.subscribe((result) => {
			this.user = result;
		});
	}

	notifyConfigSaved(): void {
		this.configSavedSource.next();
	}

	private setEndPoint(method: string): string {
		return this.getApi() + method + this.getApiKey();
	}

	private getApi(): string {
		return this.config.apiServer.endpoint;
	}

	private getApiKey(): string {
		return '&key=' + this.config.apiCredentials.clientId + '&userid=' + this.user.user.userid + this.getPackage();
	}

	private getPackage(): string {
		const p = this.globalSearchService.getSessionItem('package');
		if (p) {
			return '&package=' + p;
		} else {
			return '';
		}
	}

	private normalizeExportResponse(body: any): { success: boolean; message?: string; response?: any; request_payload?: any } {
		const payload = body?.request_payload;
		const withPayload = (o: { success: boolean; message?: string; response?: any }) =>
			payload !== undefined ? { ...o, request_payload: payload } : o;
		if (body && typeof body.success === 'boolean') {
			return withPayload({
				success: body.success,
				message: body.message,
				response: body.response ?? body.data
			});
		}
		const code = body?.code;
		const data = body?.data;
		if (code >= 200 && code < 300 && data) {
			const details = data.detailsLoaded ?? data.data?.detailsLoaded;
			if (Array.isArray(details)) {
				const msgs: string[] = [];
				for (const d of details) {
					const m = d?.msg && String(d.msg).trim();
					if (m) {
						const sku = d.sku ? String(d.sku) + ': ' : '';
						msgs.push(sku + m);
					}
				}
				if (msgs.length > 0) {
					return withPayload({
						success: false,
						message: 'Order exported with line-level errors: ' + msgs.slice(0, 5).join('; '),
						response: data
					});
				}
			}
			return withPayload({
				success: true,
				message: body.message ?? 'Order exported successfully',
				response: data
			});
		}
		return withPayload({
			success: false,
			message: body?.message ?? body?.error ?? 'Export failed',
			response: body?.data ?? body?.response ?? body
		});
	}

	getConfig(clientId?: string): Observable<Tpl3plConfig> {
		let method = 'tpl3pl/config/get';
		if (clientId) {
			method += '&client_id=' + clientId;
		}
		return this.http.get<Tpl3plConfig>(this.setEndPoint(method));
	}

	saveConfig(config: Tpl3plConfig): Observable<any> {
		const method = 'tpl3pl/config/save';
		return this.http.post(this.setEndPoint(method), config);
	}

	testConnection(config?: Tpl3plConfig): Observable<any> {
		const method = 'tpl3pl/config/test';
		const body = config || {};
		return this.http.post(this.setEndPoint(method), body);
	}

	exportOrder(orderno: string): Observable<{ success: boolean; message?: string; response?: any; request_payload?: any }> {
		const method = 'tpl3pl/orders/export';
		return this.http.post(this.setEndPoint(method), { orderno }).pipe(
			map((body: any) => this.normalizeExportResponse(body))
		);
	}

	getExportQueue(filters?: {
		status?: string;
		limit?: number;
		offset?: number;
		includeArchived?: boolean;
	}): Observable<{
		success: boolean;
		data: ExportQueueItem[];
		total: number;
		limit: number;
		offset: number;
	}> {
		let method = 'tpl3pl/orders/queue';
		if (filters) {
			if (filters.status) { method += '&status=' + filters.status; }
			if (filters.limit) { method += '&limit=' + filters.limit; }
			if (filters.offset) { method += '&offset=' + filters.offset; }
			if (filters.includeArchived) { method += '&include_archived=1'; }
		}
		return this.http.get<{
			success: boolean;
			data: ExportQueueItem[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method));
	}

	retryExport(id?: number, orderno?: string): Observable<{ success: boolean; message?: string; response?: any }> {
		const method = 'tpl3pl/orders/retry';
		const body: any = {};
		if (id) {body.id = id;}
		if (orderno) {body.orderno = orderno;}
		return this.http.post(this.setEndPoint(method), body).pipe(
			map((res: any) => this.normalizeExportResponse(res))
		);
	}

	deleteQueueItem(id: number): Observable<{ success: boolean; message?: string }> {
		const method = 'tpl3pl/orders/delete';
		return this.http.post<{ success: boolean; message?: string }>(this.setEndPoint(method), { id });
	}

	archiveQueueItem(id: number): Observable<{ success: boolean; message?: string }> {
		return this.http.post<{ success: boolean; message?: string }>(this.setEndPoint('tpl3pl/orders/archive'), { id });
	}

	archiveAllExported(): Observable<{ success: boolean; message?: string; archived_count?: number }> {
		return this.http.post<{ success: boolean; message?: string; archived_count?: number }>(this.setEndPoint('tpl3pl/orders/archiveAllExported'), {});
	}

	getExportPreview(orderno: string): Observable<{ success: boolean; request_payload?: any; message?: string }> {
		return this.http.post<{ success: boolean; request_payload?: any; message?: string }>(
			this.setEndPoint('tpl3pl/orders/preview'),
			{ orderno }
		);
	}

	processQueue(limit?: number): Observable<any> {
		const method = 'tpl3pl/orders/process';
		const body: any = {};
		if (limit) { body.limit = limit; }
		return this.http.post(this.setEndPoint(method), body);
	}

	importShipments(data: any): Observable<any> {
		const method = 'tpl3pl/shipments/import';
		return this.http.post(this.setEndPoint(method), { data });
	}

	pollShipments(dateFrom?: string, dateTo?: string): Observable<any> {
		const method = 'tpl3pl/shipments/poll';
		const body: any = {};
		if (dateFrom) { body.date_from = dateFrom; }
		if (dateTo) { body.date_to = dateTo; }
		if (!dateFrom && !dateTo) { body.date = new Date().toISOString().slice(0, 10); }
		return this.http.post(this.setEndPoint(method), body, { responseType: 'text' }).pipe(
			map((raw: string) => this.parsePollResponse(raw))
		);
	}

	private parsePollResponse(raw: string): any {
		if (typeof raw !== 'string' || raw.trim() === '') {
			return { success: false, message: 'Empty response', processed: 0 };
		}
		const secondBrace = raw.indexOf('}{');
		const jsonStr = secondBrace >= 0 ? raw.substring(secondBrace + 1) : raw;
		try {
			return JSON.parse(jsonStr);
		} catch {
			try {
				return JSON.parse(raw);
			} catch {
				return { success: false, message: 'Invalid JSON response', processed: 0 };
			}
		}
	}

	pollShipmentsByOrderNo(orderNo: string, channel: 'b2b' | 'b2c' | 'both' = 'both'): Observable<any> {
		const body: { order_no: string; channel?: string } = { order_no: orderNo.trim() };
		if (channel !== 'both') {
			body.channel = channel;
		}
		return this.http.post(this.setEndPoint('tpl3pl/shipments/poll_by_order'), body);
	}

	pollShipmentsRaw(dateFrom?: string, dateTo?: string): Observable<{ success: boolean; data?: any[]; request_payload?: any; response?: any; response_b2b?: any; response_b2c?: any; message?: string }> {
		const method = 'tpl3pl/shipments/poll_raw';
		const body: any = {};
		if (dateFrom) { body.date_from = dateFrom; }
		if (dateTo) { body.date_to = dateTo; }
		if (!dateFrom && !dateTo) { body.date = new Date().toISOString().slice(0, 10); }
		return this.http.post(this.setEndPoint(method), body, { responseType: 'text' }).pipe(
			map((raw: string) => this.parsePollResponse(raw))
		);
	}

	getNextInboundPo(): Observable<{ success: boolean; message?: string; purchaseOrderNo?: string }> {
		return this.http.post<{ success: boolean; message?: string; purchaseOrderNo?: string }>(this.setEndPoint('tpl3pl/inbound/getnextpo'), {});
	}

	getInboundPreview(purchaseOrderNo?: string): Observable<{ success: boolean; message?: string; request_payload?: { method: string; path: string; body: any } }> {
		const body: any = {};
		if (purchaseOrderNo != null && purchaseOrderNo.trim() !== '') { body.purchaseOrderNo = purchaseOrderNo.trim(); }
		return this.http.post<{ success: boolean; message?: string; request_payload?: { method: string; path: string; body: any } }>(this.setEndPoint('tpl3pl/inbound/preview'), body);
	}

	createInbound(payload: any): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/inbound/create'), { payload });
	}

	getInboundReceivedByDate(date: string): Observable<{ success: boolean; data: any[] }> {
		let method = 'tpl3pl/inbound/received';
		method += '&date=' + encodeURIComponent(date);
		return this.http.get<{ success: boolean; data: any[] }>(this.setEndPoint(method));
	}

	getInboundReceivedByPo(orderNo: string): Observable<{ success: boolean; data: any[] }> {
		let method = 'tpl3pl/inbound/receivedpo';
		method += '&orderNo=' + encodeURIComponent(orderNo);
		return this.http.get<{ success: boolean; data: any[] }>(this.setEndPoint(method));
	}

	getOpenB2BOrders(): Observable<{ success: boolean; data: any[] }> {
		return this.http.get<{ success: boolean; data: any[] }>(this.setEndPoint('tpl3pl/outbound/open'));
	}

	getOpenB2BOrdersRaw(): Observable<{ success: boolean; data: any[]; request_payload?: any; response?: any; message?: string }> {
		return this.http.get<{ success: boolean; data: any[]; request_payload?: any; response?: any; message?: string }>(this.setEndPoint('tpl3pl/outbound/open_raw'));
	}

	getSkuPushPreview(stockid?: string): Observable<{ success: boolean; message?: string; request_payload?: any; stockid?: string }> {
		const body: any = {};
		if (stockid) { body.stockid = stockid; }
		return this.http.post<{ success: boolean; message?: string; request_payload?: any; stockid?: string }>(this.setEndPoint('tpl3pl/sku/push_preview'), body);
	}

	pushOneSku(stockid: string): Observable<{ success: boolean; message?: string; request_payload?: any; response?: any }> {
		return this.http.post<{ success: boolean; message?: string; request_payload?: any; response?: any }>(this.setEndPoint('tpl3pl/sku/push_one'), { stockid });
	}

	getSkuPushList(offset: number, limit: number, search?: string): Observable<{ success: boolean; stockids: string[]; items?: { stockid: string; description: string }[]; total: number }> {
		const body: { offset: number; limit: number; search?: string } = { offset, limit };
		if (search != null && search.trim() !== '') { body.search = search.trim(); }
		return this.http.post<{ success: boolean; stockids: string[]; items?: { stockid: string; description: string }[]; total: number }>(this.setEndPoint('tpl3pl/sku/push_list'), body);
	}

	pushSkuBatch(offset: number, limit: number): Observable<{
		success: boolean;
		created?: number;
		updated?: number;
		failed?: number;
		offset?: number;
		responses?: Array<{ stockid: string; success: boolean; raw_response: any }>;
	}> {
		return this.http.post<{
			success: boolean;
			created?: number;
			updated?: number;
			failed?: number;
			offset?: number;
			responses?: Array<{ stockid: string; success: boolean; raw_response: any }>;
		}>(this.setEndPoint('tpl3pl/sku/push_batch'), { offset, limit });
	}

	createSku(payload: any): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/sku/create'), { payload });
	}

	deleteSku(sku: string): Observable<{ success: boolean; message?: string }> {
		return this.http.post<{ success: boolean; message?: string }>(this.setEndPoint('tpl3pl/sku/delete'), { sku });
	}

	startSkuSync(removeFromPgs?: boolean): Observable<{ success: boolean; job_id?: number; message?: string }> {
		const body: any = {};
		if (removeFromPgs) { body.remove_from_pgs = true; }
		return this.http.post<{ success: boolean; job_id?: number; message?: string }>(this.setEndPoint('tpl3pl/sku/start'), body);
	}

	getSkuSyncStatus(): Observable<{
		success: boolean;
		job: {
			id: number;
			status: string;
			total: number;
			current_offset: number;
			created: number;
			updated: number;
			deleted: number;
			failed: number;
			errors: Array<{ stockid: string; message: string }>;
			remove_from_pgs: boolean;
			phase: string;
			started_at: string | null;
			completed_at: string | null;
			created_at: string;
		} | null;
	}> {
		return this.http.get<{
			success: boolean;
			job: {
				id: number;
				status: string;
				total: number;
				current_offset: number;
				created: number;
				updated: number;
				deleted: number;
				failed: number;
				errors: Array<{ stockid: string; message: string }>;
				remove_from_pgs: boolean;
				phase: string;
				started_at: string | null;
				completed_at: string | null;
				created_at: string;
			} | null;
		}>(this.setEndPoint('tpl3pl/sku/syncStatus'));
	}

	syncSkusToPgs(params?: { offset?: number; phase?: 'sync' | 'remove' }): Observable<{
		success: boolean;
		created?: number;
		updated?: number;
		deleted?: number;
		failed: number;
		errors: Array<{ stockid: string; message: string }>;
		total: number;
		offset: number;
		next_offset?: number;
		done: boolean;
		message?: string;
		phase?: 'sync' | 'remove';
	}> {
		const body = params || {};
		return this.http.post<{
			success: boolean;
			created?: number;
			updated?: number;
			deleted?: number;
			failed: number;
			errors: Array<{ stockid: string; message: string }>;
			total: number;
			offset: number;
			next_offset?: number;
			done: boolean;
			message?: string;
			phase?: 'sync' | 'remove';
		}>(this.setEndPoint('tpl3pl/sku/sync'), body);
	}

	getShipments(filters?: {
		orderno?: string;
		limit?: number;
		offset?: number;
	}): Observable<{
		success: boolean;
		data: Shipment[];
		total: number;
		limit: number;
		offset: number;
	}> {
		let method = 'tpl3pl/shipments/list';
		if (filters) {
			if (filters.orderno) {method += '&orderno=' + filters.orderno;}
			if (filters.limit) {method += '&limit=' + filters.limit;}
			if (filters.offset) {method += '&offset=' + filters.offset;}
		}
		return this.http.get<{
			success: boolean;
			data: Shipment[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method));
	}

	getInventorySyncPreview(warehouse?: string): Observable<{ success: boolean; message?: string; request_payload?: any }> {
		const method = 'tpl3pl/inventory/sync_preview';
		const body: any = {};
		if (warehouse) { body.warehouse = warehouse; }
		return this.http.post<{ success: boolean; message?: string; request_payload?: any }>(this.setEndPoint(method), body);
	}

	syncInventory(warehouse?: string): Observable<any> {
		const method = 'tpl3pl/inventory/sync';
		const body: any = {};
		if (warehouse) {body.warehouse = warehouse;}
		return this.http.post(this.setEndPoint(method), body);
	}

	clearInventorySync(): Observable<{ success: boolean; message?: string; deleted?: number }> {
		return this.http.post<{ success: boolean; message?: string; deleted?: number }>(this.setEndPoint('tpl3pl/inventory/clear'), {});
	}

	getInventoryStatus(warehouse?: string, cacheBust?: boolean): Observable<any> {
		const method = 'tpl3pl/inventory/status';
		let params = new HttpParams();
		if (warehouse) {
			params = params.set('warehouse', warehouse);
		}
		if (cacheBust) {
			params = params.set('_t', Date.now().toString());
		}
		const url = this.setEndPoint(method);
		return this.http.get(url, { params });
	}

	getInventory(filters?: {
		warehouse?: string;
		stockid?: string;
		status?: string;
		search?: string;
		sort?: string;
		order?: 'asc' | 'desc';
		limit?: number;
		offset?: number;
		cacheBust?: boolean;
	}): Observable<{
		success: boolean;
		data: InventoryItem[];
		total: number;
		limit: number;
		offset: number;
	}> {
		const method = 'tpl3pl/inventory/list';
		let params = new HttpParams();
		if (filters) {
			if (filters.warehouse) { params = params.set('warehouse', filters.warehouse); }
			if (filters.stockid) { params = params.set('stockid', filters.stockid); }
			if (filters.status) { params = params.set('status', filters.status); }
			if (filters.search != null && filters.search !== '') { params = params.set('search', filters.search); }
			if (filters.sort) { params = params.set('sort', filters.sort); }
			if (filters.order) { params = params.set('order', filters.order); }
			if (filters.limit != null) { params = params.set('limit', String(filters.limit)); }
			if (filters.offset != null) { params = params.set('offset', String(filters.offset)); }
			if (filters.cacheBust) { params = params.set('_t', Date.now().toString()); }
		}
		return this.http.get<{
			success: boolean;
			data: InventoryItem[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method), { params });
	}

	getLogs(filters?: {
		log_type?: string;
		status?: string;
		orderno?: string;
		from_date?: string;
		to_date?: string;
		limit?: number;
		offset?: number;
	}): Observable<{
		success: boolean;
		data: LogEntry[];
		total: number;
		limit: number;
		offset: number;
	}> {
		let method = 'tpl3pl/logs';
		if (filters) {
			if (filters.log_type) {method += '&log_type=' + filters.log_type;}
			if (filters.status) {method += '&status=' + filters.status;}
			if (filters.orderno) {method += '&orderno=' + filters.orderno;}
			if (filters.from_date) {method += '&from_date=' + filters.from_date;}
			if (filters.to_date) {method += '&to_date=' + filters.to_date;}
			if (filters.limit) {method += '&limit=' + filters.limit;}
			if (filters.offset) {method += '&offset=' + filters.offset;}
		}
		return this.http.get<{
			success: boolean;
			data: LogEntry[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method));
	}

	getLogsPost(filters?: {
		log_type?: string;
		status?: string;
		orderno?: string;
		from_date?: string;
		to_date?: string;
		limit?: number;
		offset?: number;
	}): Observable<{
		success: boolean;
		data: LogEntry[];
		total: number;
		limit: number;
		offset: number;
	}> {
		const method = 'tpl3pl/logs';
		return this.http.post<{
			success: boolean;
			data: LogEntry[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method), filters || {});
	}

	getStatus(): Observable<any> {
		const method = 'tpl3pl/status';
		return this.http.get(this.setEndPoint(method));
	}
}
